import { remark } from 'remark';
import remarkGfm from 'remark-gfm';
import { SKIP, visit } from 'unist-util-visit';

import { encodeSpacesInLinkUrls } from '@/lib/utils/markdown-sanitizer';

import { tokenizeWords } from './alignment-utils';

type MdastNode = { type: string; value?: string; children?: MdastNode[] };

const markdownProcessor = remark().use(remarkGfm);

// Returns the plain-text content of an inline node (text, emphasis, links, etc.).
// Images and raw HTML are silently dropped — no word substitutions so that
// ElevenLabs character-level alignment stays in sync with what is displayed.
function inlineToText(node: MdastNode): string {
  if (node.type === 'text' || node.type === 'inlineCode') {
    return (node.value ?? '').replace(
      /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{FE00}-\u{FEFF}]/gu,
      '',
    );
  }
  if (node.type === 'image' || node.type === 'html') return '';
  return node.children?.map(inlineToText).join('') ?? '';
}

export function partToText(markdownText: string): string {
  // A markdown link destination with raw spaces (e.g. `?hotelBoard=לינה בלבד`)
  // fails to parse as a link node, leaking the raw URL into the text where
  // ElevenLabs reads it character-by-character. Reuse the same sanitizer the
  // display path applies so the link parses and only its label is read.
  const tree = markdownProcessor.parse(encodeSpacesInLinkUrls(markdownText));
  const blocks: string[] = [];

  visit(tree, node => {
    switch (node.type) {
      case 'code':
      case 'thematicBreak':
      case 'html':
      case 'table':
        return SKIP;
      case 'paragraph':
      case 'heading': {
        const text = inlineToText(node as unknown as MdastNode).trim();
        if (text) blocks.push(text);
        return SKIP;
      }
    }
  });

  return blocks.join('. ');
}

// Counts the words ElevenLabs will speak in the given TTS-ready plain text.
// Uses the same tokeniser as buildWordSegments / highlightWordInDom, so
// analytics counts match what the adapter will actually highlight.
export function countTtsWords(text: string): number {
  if (!text.trim()) return 0;
  return tokenizeWords(text).length;
}
